/**
 * TrackingDataPreserver
 *
 * Erhält Tracking-Daten (Referrer, UTM-Parameter, etc.) wenn die Seite
 * nach einem Cookie-Consent neu geladen wird (CSP-bedingt).
 *
 * Dies ist wichtig für Dienste wie Google Analytics, Matomo, Facebook, etc.,
 * da sonst wichtige Tracking-Informationen beim Reload verloren gehen würden.
 */
import { pmLog, pmError } from './Logger.js';

export class TrackingDataPreserver {
    constructor() {
        this.storageKey = 'pixelmate_tracking_data';
        this.restored = false;
    }

    /**
     * Speichert alle relevanten Tracking-Daten vor einem Page-Reload
     *
     * Erfasst:
     * - Referrer (woher kam der User?)
     * - Landing Page (aktuelle URL)
     * - UTM-Parameter (Marketing-Kampagnen)
     * - Google Ads Click ID (gclid)
     * - Facebook Click ID (fbclid)
     * - Microsoft Click ID (msclkid)
     * - Timestamp
     */
    saveTrackingDataBeforeReload() {
        try {
            const urlParams = new URLSearchParams(window.location.search);

            const trackingData = {
                referrer: document.referrer,
                landingPage: window.location.href,
                timestamp: Date.now(),

                // UTM-Parameter (Standard für alle Marketing-Tools)
                utm_source: urlParams.get('utm_source'),
                utm_medium: urlParams.get('utm_medium'),
                utm_campaign: urlParams.get('utm_campaign'),
                utm_term: urlParams.get('utm_term'),
                utm_content: urlParams.get('utm_content'),

                // Click IDs verschiedener Plattformen
                gclid: urlParams.get('gclid'),      // Google Ads
                fbclid: urlParams.get('fbclid'),    // Facebook
                msclkid: urlParams.get('msclkid'),  // Microsoft Ads

                // Weitere mögliche Parameter
                ttclid: urlParams.get('ttclid'),    // TikTok
                twclid: urlParams.get('twclid'),    // Twitter
                li_fat_id: urlParams.get('li_fat_id'), // LinkedIn

                // Query-String komplett speichern für Fallback
                search: window.location.search
            };

            sessionStorage.setItem(this.storageKey, JSON.stringify(trackingData));
            pmLog('Tracking data saved before reload:', trackingData);

            return true;
        } catch (e) {
            pmError('Failed to save tracking data:', e);
            return false;
        }
    }

    /**
     * Stellt Tracking-Daten nach einem Page-Reload wieder her
     * und übergibt sie an die entsprechenden Tracking-Dienste
     */
    restoreTrackingData() {
        if (this.restored) {
            pmLog('Tracking data already restored');
            return;
        }

        try {
            const data = sessionStorage.getItem(this.storageKey);

            if (!data) {
                pmLog('No tracking data to restore');
                return;
            }

            const trackingData = JSON.parse(data);
            pmLog('Restoring tracking data:', trackingData);

            // An verschiedene Tracking-Dienste übergeben
            this.restoreToGoogleAnalytics(trackingData);
            this.restoreToMatomo(trackingData);
            this.restoreToFacebookPixel(trackingData);
            this.restoreToGoogleTagManager(trackingData);

            // Cleanup - Daten nach Wiederherstellung löschen
            sessionStorage.removeItem(this.storageKey);
            this.restored = true;

            pmLog('Tracking data successfully restored and cleaned up');
        } catch (e) {
            pmError('Failed to restore tracking data:', e);
        }
    }

    /**
     * Übergibt Tracking-Daten an Google Analytics (gtag.js)
     */
    restoreToGoogleAnalytics(trackingData) {
        // Warten auf gtag-Verfügbarkeit
        this.waitForTracking('gtag', () => {
            try {
                // Campaign-Daten setzen
                const campaignData = {};

                if (trackingData.utm_source) campaignData.campaign_source = trackingData.utm_source;
                if (trackingData.utm_medium) campaignData.campaign_medium = trackingData.utm_medium;
                if (trackingData.utm_campaign) campaignData.campaign_name = trackingData.utm_campaign;
                if (trackingData.utm_term) campaignData.campaign_term = trackingData.utm_term;
                if (trackingData.utm_content) campaignData.campaign_content = trackingData.utm_content;

                // Referrer setzen
                if (trackingData.referrer) {
                    campaignData.page_referrer = trackingData.referrer;
                }

                // Google Click ID setzen
                if (trackingData.gclid) {
                    campaignData.gclid = trackingData.gclid;
                }

                if (Object.keys(campaignData).length > 0) {
                    window.gtag('set', campaignData);
                    pmLog('Google Analytics: Tracking data restored', campaignData);
                }
            } catch (e) {
                pmError('Failed to restore Google Analytics tracking data:', e);
            }
        });
    }

    /**
     * Übergibt Tracking-Daten an Matomo (Piwik)
     */
    restoreToMatomo(trackingData) {
        this.waitForTracking('_paq', () => {
            try {
                const _paq = window._paq;

                // Referrer setzen
                if (trackingData.referrer) {
                    _paq.push(['setReferrerUrl', trackingData.referrer]);
                }

                // Custom URL mit allen Parametern setzen
                if (trackingData.landingPage) {
                    _paq.push(['setCustomUrl', trackingData.landingPage]);
                }

                // Campaign-Daten setzen
                if (trackingData.utm_campaign && trackingData.utm_source) {
                    _paq.push([
                        'setCampaignNameKey', 'utm_campaign',
                        'setCampaignKeywordKey', 'utm_term'
                    ]);
                }

                pmLog('Matomo: Tracking data restored');
            } catch (e) {
                pmError('Failed to restore Matomo tracking data:', e);
            }
        });
    }

    /**
     * Übergibt Tracking-Daten an Facebook Pixel
     */
    restoreToFacebookPixel(trackingData) {
        this.waitForTracking('fbq', () => {
            try {
                // Facebook speichert fbclid automatisch, aber wir können
                // zusätzliche Parameter über Custom Events übergeben
                if (trackingData.fbclid) {
                    window.fbq('track', 'PageView', {
                        fbclid: trackingData.fbclid,
                        referrer: trackingData.referrer
                    });
                    pmLog('Facebook Pixel: Tracking data restored');
                }
            } catch (e) {
                pmError('Failed to restore Facebook Pixel tracking data:', e);
            }
        });
    }

    /**
     * Übergibt Tracking-Daten an Google Tag Manager
     */
    restoreToGoogleTagManager(trackingData) {
        this.waitForTracking('dataLayer', () => {
            try {
                window.dataLayer = window.dataLayer || [];

                window.dataLayer.push({
                    event: 'trackingDataRestored',
                    originalReferrer: trackingData.referrer,
                    originalLandingPage: trackingData.landingPage,
                    campaignSource: trackingData.utm_source,
                    campaignMedium: trackingData.utm_medium,
                    campaignName: trackingData.utm_campaign,
                    campaignTerm: trackingData.utm_term,
                    campaignContent: trackingData.utm_content,
                    gclid: trackingData.gclid,
                    fbclid: trackingData.fbclid
                });

                pmLog('Google Tag Manager: Tracking data restored');
            } catch (e) {
                pmError('Failed to restore GTM tracking data:', e);
            }
        });
    }

    /**
     * Wartet auf Verfügbarkeit eines Tracking-Objekts
     * und führt dann den Callback aus
     *
     * @param {string} objectName - Name des globalen Objekts (z.B. 'gtag', '_paq')
     * @param {function} callback - Funktion die ausgeführt werden soll
     * @param {number} timeout - Max. Wartezeit in ms (default: 10000)
     */
    waitForTracking(objectName, callback, timeout = 10000) {
        const startTime = Date.now();

        const checkInterval = setInterval(() => {
            if (window[objectName]) {
                clearInterval(checkInterval);
                callback();
            } else if (Date.now() - startTime > timeout) {
                clearInterval(checkInterval);
                pmLog(`Timeout waiting for ${objectName}`);
            }
        }, 100);
    }
}

